import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { 
  Plus, 
  Search, 
  FileText, 
  Rocket, 
  BarChart3, 
  Users, 
  Settings, 
  Sparkles,
  LightbulbIcon,
  Orbit,
  BrainCircuit,
  Target,
  Building2
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { CreateProjectForm } from "@/components/projects/CreateProjectForm";
import { OnboardingWizard } from "@/components/onboarding/OnboardingWizard";
import { FeatureTour } from "@/components/onboarding/FeatureTour";
import { SetupTips } from "@/components/onboarding/SetupTips";
import { SuccessAnimation } from "@/components/onboarding/SuccessAnimation";
import { EnhancedProjectCard } from "@/components/projects/EnhancedProjectCard";
import { motion, AnimatePresence } from "framer-motion";

interface ProjectsProps {
  initialOnboarding?: boolean;
}

const Projects = ({ initialOnboarding = false }: ProjectsProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isOnboardingActive, setIsOnboardingActive] = useState(initialOnboarding);
  const [currentWizardStep, setCurrentWizardStep] = useState(0);
  const [isFeatureTourActive, setIsFeatureTourActive] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [newProjectId, setNewProjectId] = useState<string>("");
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // For background animation
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const projectsContainerRef = useRef<HTMLDivElement>(null);

  // Track mouse position for dynamic effects
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (projectsContainerRef.current) {
        const rect = projectsContainerRef.current.getBoundingClientRect();
        setMousePosition({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top
        });
      }
    };
    
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => {
      // First fetch all projects
      const { data: projectsData, error: projectsError } = await supabase
        .from("projects")
        .select("*")
        .order("created_at", { ascending: false });

      if (projectsError) throw projectsError;
      
      // Now fetch content counts for each project
      // We'll fetch all generated content and count manually
      const countMap = new Map();
      
      // Only fetch content data if user is authenticated
      if (user?.id) {
        const { data: contentData, error: contentError } = await supabase
          .from("generated_content")
          .select('project_id')
          .eq('user_id', user.id);
          
        if (contentError) throw contentError;
        
        // Count content for each project
        contentData?.forEach(item => {
          const pid = item.project_id;
          countMap.set(pid, (countMap.get(pid) || 0) + 1);
        });
      }
      
      // Update each project with accurate content count (stored as client-side metadata)
      return projectsData.map(project => ({
        ...project,
        content_count: countMap.get(project.id) || 0,
        tasks_completed: 0 // We'll track this client-side
      }));
    },
  });

  // Check if user has no projects - show welcome page
  useEffect(() => {
    // If initialOnboarding is set, we're already showing the onboarding wizard
    if (initialOnboarding) {
      setIsOnboardingActive(true);
      return;
    }
    
    // Check for onboarding flag in localStorage (set during signup)
    const shouldShowOnboarding = localStorage.getItem("showOnboarding") === "true";
    if (shouldShowOnboarding) {
      localStorage.removeItem("showOnboarding"); // Remove the flag
      setIsOnboardingActive(true);
      return;
    }
    
    // No automatic onboarding for users with no projects
    // They'll see the "No Projects Yet" UI instead
  }, [initialOnboarding]);

  // Check if user just created their first project - show feature tour
  useEffect(() => {
    const hasJustCreatedProject = localStorage.getItem("justCreatedFirstProject");
    if (hasJustCreatedProject === "true" && projects.length > 0 && !isFeatureTourActive) {
      localStorage.removeItem("justCreatedFirstProject");
      setTimeout(() => {
        setIsFeatureTourActive(true);
      }, 1000);
    }
  }, [projects.length, isFeatureTourActive]);

  const createProject = useMutation({
    mutationFn: async (values: any) => {
      // First create the project
      const { data, error } = await supabase
        .from("projects")
        .insert([{ 
          name: values.businessName, // Use business name as project name
          business_name: values.businessName,
          company_type: values.companyType,
          industry: values.industry,
          user_id: user?.id,
          created_at: new Date().toISOString(),
        }])
        .select()
        .single();

      if (error) throw error;
      
      // If we have analysis results (from onboarding flow), save them
      if (values.analysisResults) {
        const { error: analysisError } = await supabase
          .from("website_crawls")
          .insert({
            project_id: data.id,
            user_id: user?.id,
            url: values.websiteUrl || "https://example.com", // Use provided URL or a default
            analysis_results: values.analysisResults,
            created_at: new Date().toISOString(),
            status: 'completed'
          });
          
        if (analysisError) {
          console.error("Error saving analysis results:", analysisError);
          // We'll continue anyway as the project was created successfully
        }
      }
      
      // Add client-side metadata
      return {
        ...data,
        content_count: 0,
        tasks_completed: 0
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      
      if (isOnboardingActive) {
        // Set projectId first, then show animation to avoid timing issues
        setNewProjectId(data.id);
        // Only then show success animation
        setShowSuccessAnimation(true);
        
        // Set flag for first project creation
        if (projects.length === 0) {
          localStorage.setItem("justCreatedFirstProject", "true");
        }
      } else {
        toast({
          title: "Project created",
          description: "Your new project has been created successfully.",
        });
        setIsDialogOpen(false);
      }
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create project. Please try again.",
        variant: "destructive",
      });
      console.error("Error creating project:", error);
    },
  });

  const filteredProjects = projects.filter((project) =>
    project.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreateProject = (values: any) => {
    // If this came from the onboarding flow and already has an ID, just navigate to it
    if (values.projectId) {
      navigateToPlaybook(values.projectId);
      return;
    }
    
    // Otherwise create a new project
    createProject.mutate(values);
  };

  const handleCompleteFeatureTour = () => {
    setIsFeatureTourActive(false);
    toast({
      title: "Welcome aboard!",
      description: "You're all set to start creating viral content.",
    });
  };

  const navigateToPlaybook = (projectId: string) => {
    navigate(`/playbooks`);
  };

  const handleSuccessAnimationComplete = (projectId: string) => {
    // Navigate to playbooks after project creation
    navigate(`/playbooks`, { 
      state: { fromOnboarding: true, isNewProject: true } 
    });
  };

  // If onboarding is active, show onboarding flow
  if (isOnboardingActive) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-950">
        <div className="container mx-auto px-4 py-10">
          {showSuccessAnimation && (
            <SuccessAnimation 
              title="Your Project is Ready!"
              message="Get ready to supercharge your content marketing."
              projectId={newProjectId}
              onComplete={handleSuccessAnimationComplete}
            />
          )}
          
          {!showSuccessAnimation && (
            <div className="w-full">
              <OnboardingWizard
                onComplete={handleCreateProject}
                isLoading={createProject.isPending}
                onStepChange={(step) => {
                  setCurrentWizardStep(step);
                  console.log("Wizard step changed to:", step);
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative p-0 overflow-hidden" ref={projectsContainerRef}>
      {/* Background decorations */}
      <div className="absolute inset-0 z-0 opacity-50 overflow-hidden">
        <div className="absolute top-0 right-0 rounded-full w-96 h-96 bg-gradient-to-r from-blue-300/20 to-blue-200/20 blur-3xl -translate-y-1/2 translate.x-1/3 dark:from-blue-900/20 dark:to-blue-800/20"></div>
        <div className="absolute bottom-20 left-0 rounded-full w-80 h-80 bg-gradient-to-r from-amber-300/20 to-orange-200/20 blur-3xl translate-y-1/3 -translate-x-1/2 dark:from-amber-900/20 dark:to-orange-800/20"></div>
      </div>
      
      <div 
        className="absolute inset-0 z-0 opacity-[0.015] dark:opacity-[0.03]"
        style={{
          backgroundImage: "radial-gradient(circle at 15px 15px, rgba(60, 60, 60, 0.2) 2px, transparent 0)",
          backgroundSize: "30px 30px"
        }}
      ></div>
      
      {/* Feature Tour */}
      <FeatureTour 
        isOpen={isFeatureTourActive}
        onComplete={handleCompleteFeatureTour}
        onSkip={() => setIsFeatureTourActive(false)}
      />
      
      <div className="relative z-10 p-8">
        <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-50 dark:to-gray-300 bg-clip-text text-transparent mb-2">
              Your Projects
            </h1>
            <p className="text-sm md:text-base text-zinc-600 dark:text-zinc-400 max-w-2xl">
              Create and manage your content marketing campaigns. Each project lets you generate tailored content for your specific business needs.
            </p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <Button onClick={() => setIsDialogOpen(true)} className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground font-medium shadow-sm hover:shadow">
              <Plus className="mr-2 h-4 w-4" />
              New Project
            </Button>
            <DialogContent className="sm:max-w-[650px] p-0 overflow-hidden bg-white dark:bg-zinc-900 rounded-xl border-0 shadow-xl">
              <div className="bg-gradient-to-r from-zinc-50 to-zinc-100 dark:from-zinc-800 dark:to-zinc-900 p-6 border-b border-zinc-200 dark:border-zinc-700">
                <DialogHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2.5 rounded-xl bg-blue-100 dark:bg-blue-900/30">
                      <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <DialogTitle className="text-xl font-semibold text-zinc-900 dark:text-white">Create New Project</DialogTitle>
                  </div>
                  <DialogDescription className="text-zinc-500 dark:text-zinc-400">
                    Fill in the details below to create your new content marketing project. This will help us tailor content generation to your specific business needs.
                  </DialogDescription>
                </DialogHeader>
              </div>
              <div className="p-6">
                <CreateProjectForm 
                  onSubmit={handleCreateProject}
                  isLoading={createProject.isPending}
                />
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mb-8 max-w-md">
          <div className="relative rounded-lg overflow-hidden shadow-sm group transition-all border border-zinc-200 dark:border-zinc-700 focus-within:border-blue-400 dark:focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-400 dark:focus-within:ring-blue-500">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 group-focus-within:text-blue-500" />
            <Input
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border-0 pl-10 py-6 bg-white dark:bg-zinc-800 focus-visible:ring-0"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-[280px] rounded-xl bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 animate-pulse overflow-hidden">
                <div className="p-6 flex flex-col h-full">
                  <div className="h-6 bg-zinc-200 dark:bg-zinc-700 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-zinc-200 dark:bg-zinc-700 rounded w-1/2 mb-6"></div>
                  
                  <div className="h-16 bg-zinc-200 dark:bg-zinc-700 rounded mb-6"></div>
                  
                  <div className="h-4 bg-zinc-200 dark:bg-zinc-700 rounded w-full mb-3"></div>
                  <div className="h-2 bg-zinc-200 dark:bg-zinc-700 rounded w-full mb-6"></div>
                  
                  <div className="mt-auto flex justify-between">
                    <div className="h-4 bg-zinc-200 dark:bg-zinc-700 rounded w-1/3"></div>
                    <div className="h-4 bg-zinc-200 dark:bg-zinc-700 rounded w-1/4"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredProjects.length === 0 ? (
          <Card className="text-center p-12 border border-dashed">
            <CardContent className="p-0">
              <div className="mx-auto max-w-md">
                <div className="rounded-full bg-blue-100 dark:bg-blue-900/30 p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Rocket className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                </div>
                <CardTitle className="text-2xl font-bold mb-3">No Projects Yet</CardTitle>
                <CardDescription className="text-base mb-8">
                  Create your first project to get started with content generation. We'll guide you through the entire process.
                </CardDescription>
                <Button 
                  onClick={() => setIsDialogOpen(true)} 
                  size="lg" 
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium px-6 shadow-sm hover:shadow"
                >
                  <Plus className="mr-2 h-5 w-5" />
                  Create Your First Project
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {filteredProjects.map((project, index) => (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <EnhancedProjectCard
                      id={project.id}
                      name={project.name}
                      businessName={project.business_name}
                      industry={project.industry}
                      companyType={project.company_type}
                      employeeCount={project.employee_count}
                      createdAt={project.created_at}
                      isNew={index === 0 && new Date(project.created_at) > new Date(Date.now() - 86400000)} // New if created within last 24h
                      stats={{
                        contentCount: project.content_count || 0,
                        tasksCompleted: project.tasks_completed || 0,
                        totalTasks: 7,
                      }}
                    />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        )}
      </div>
      
      {/* Settings Feature for Tour */}
      <div id="settings-feature" className="hidden">{/* Hidden element for feature tour */}</div>
    </div>
  );
};

export default Projects;